'use client';

import { createClient } from '@/lib/supabase/client';

export interface NavigationItem {
  id: string;
  label: string;
  url: string;
  parentId?: string | null;
  displayOrder: number;
  isActive: boolean;
  icon?: string | null;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  children?: NavigationItem[];
}

export interface NavigationItemInput {
  label: string;
  url: string;
  parentId?: string | null;
  displayOrder?: number;
  isActive?: boolean;
  icon?: string | null;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

function buildNestedMenu(items: NavigationItem[]): NavigationItem[] {
  const itemMap = new Map<string, NavigationItem>();
  const rootItems: NavigationItem[] = [];

  // First pass: create map of all items
  items.forEach((item) => {
    itemMap.set(item.id, { ...item, children: [] });
  });

  // Second pass: build hierarchy
  items.forEach((item) => {
    const mappedItem = itemMap.get(item.id)!;
    if (item.parentId) {
      const parent = itemMap.get(item.parentId);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(mappedItem);
      } else {
        rootItems.push(mappedItem);
      }
    } else {
      rootItems.push(mappedItem);
    }
  });

  return rootItems;
}

export const navigationService = {
  async getAll(): Promise<NavigationItem[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('navigation_items')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (data || []).map((row) => ({
        id: row.id,
        label: row.label,
        url: row.url,
        parentId: row.parent_id,
        displayOrder: row.display_order,
        isActive: row.is_active,
        icon: row.icon,
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));
    } catch (error: any) {
      console.error('Get navigation items error:', error.message);
      throw error;
    }
  },

  async getActive(): Promise<NavigationItem[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('navigation_items')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      const items = (data || []).map((row) => ({
        id: row.id,
        label: row.label,
        url: row.url,
        parentId: row.parent_id,
        displayOrder: row.display_order,
        isActive: row.is_active,
        icon: row.icon,
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));

      return buildNestedMenu(items);
    } catch (error: any) {
      console.error('Get active navigation items error:', error.message);
      return [];
    }
  },

  async getById(id: string): Promise<NavigationItem | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('navigation_items')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return null;
        }
      }

      if (!data) return null;

      return {
        id: data.id,
        label: data.label,
        url: data.url,
        parentId: data.parent_id,
        displayOrder: data.display_order,
        isActive: data.is_active,
        icon: data.icon,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.error('Get navigation item by ID error:', error.message);
      throw error;
    }
  },

  async create(input: NavigationItemInput): Promise<NavigationItem | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('navigation_items')
        .insert({
          label: input.label,
          url: input.url,
          parent_id: input.parentId || null,
          display_order: input.displayOrder || 0,
          is_active: input.isActive !== undefined ? input.isActive : true,
          icon: input.icon || null,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return null;
        }
      }

      if (!data) return null;

      return {
        id: data.id,
        label: data.label,
        url: data.url,
        parentId: data.parent_id,
        displayOrder: data.display_order,
        isActive: data.is_active,
        icon: data.icon,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.error('Create navigation item error:', error.message);
      throw error;
    }
  },

  async update(id: string, input: Partial<NavigationItemInput>): Promise<NavigationItem | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const updateData: any = {
        updated_at: new Date().toISOString(),
      };

      if (input.label !== undefined) updateData.label = input.label;
      if (input.url !== undefined) updateData.url = input.url;
      if (input.parentId !== undefined) updateData.parent_id = input.parentId;
      if (input.displayOrder !== undefined) updateData.display_order = input.displayOrder;
      if (input.isActive !== undefined) updateData.is_active = input.isActive;
      if (input.icon !== undefined) updateData.icon = input.icon;

      const { data, error } = await supabase
        .from('navigation_items')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return null;
        }
      }

      if (!data) return null;

      return {
        id: data.id,
        label: data.label,
        url: data.url,
        parentId: data.parent_id,
        displayOrder: data.display_order,
        isActive: data.is_active,
        icon: data.icon,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.error('Update navigation item error:', error.message);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('navigation_items')
        .delete()
        .eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
        }
      }
    } catch (error: any) {
      console.error('Delete navigation item error:', error.message);
      throw error;
    }
  },

  async reorder(items: { id: string; displayOrder: number }[]): Promise<void> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      for (const item of items) {
        const { error } = await supabase
          .from('navigation_items')
          .update({ display_order: item.displayOrder, updated_at: new Date().toISOString() })
          .eq('id', item.id);

        if (error) {
          if (isSchemaError(error)) {
            console.error('Schema error:', error.message);
            throw error;
          } else {
            console.log('Data error:', error.message);
          }
        }
      }
    } catch (error: any) {
      console.error('Reorder navigation items error:', error.message);
      throw error;
    }
  },
};